import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { List, ListRowRenderer } from 'react-virtualized';
import { CheckIcon, XIcon, EyeIcon, EyeOffIcon, SearchIcon } from '@heroicons/react/solid';
import { BtnTool } from './btnTool';
import { compare, uniqueArray } from 'core/utils/utils';
import styles from './SearchBox.module.scss';
import { Box, Checkbox, Input, LinearProgress, Typography } from '@material-ui/core';
import { SGSButton, SGSTooltip } from 'components';
import classNames from 'classnames';
import { delay } from 'lodash';

type Props = {
    name: string;
    list: any;
    selected: option[];
    setSelected: Function;
    isLoading: boolean;
    noOptionLabel?: string;
    hasError?: boolean;
    displayTools?: boolean;
};

type option = {
    value: any;
    label: string;
};
const NoOptionSelected = ({ onClick, label }: { onClick: Function; label: string }) => {
    return (
        <Box className="text-center">
            <Typography className="text-center mt-4 mb-2">Aucune option sélectionnée</Typography>
            <SGSButton type="button" onClick={() => onClick()}>
                {label}
            </SGSButton>
        </Box>
    );
};

export const ListWithSearch: React.FC<PropsWithChildren<Props>> = ({ name, list, selected, setSelected, isLoading, noOptionLabel = 'Aucune option disponible', hasError, displayTools = true }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentSelection, setCurrentSelection] = useState<option[]>([]);
    const [displaySelected, setDisplaySeleted] = useState(false);

    const textInput = useRef(null);

    selected.sort(compare);

    let filteredList: option[] = [];

    if (displaySelected) filteredList = currentSelection;
    else {
        filteredList = list ? list.filter((obj: option) => obj.label.includes(searchTerm.toUpperCase())) : [];
    }

    useEffect(() => {
        if (displaySelected) setCurrentSelection(selected);
    }, [selected, displaySelected]);

    const handleDisplaySelected = () => {
        setSearchTerm('');
        setCurrentSelection(selected);
        setDisplaySeleted(!displaySelected);
    };
    const focusInput = () => {
        textInput.current.focus();
    };
    const handleSearchClick = () => {
        if (!displaySelected) return;
        handleDisplaySelected();
        delay(focusInput, 10);
    };
    const handleCheckAll = () => {
        const data: option[] = filteredList.concat(selected);
        const uniqueObjArray = uniqueArray({ data, key: 'value' });
        setSelected(uniqueObjArray);
    };
    const handleUnCheckAll = () => {
        const remainSelected = selected.filter((el) => !filteredList.includes(el));
        const uniqueObjArray = uniqueArray({ data: remainSelected, key: 'value' });
        setCurrentSelection(uniqueObjArray);
        setSelected(uniqueObjArray);
    };
    const handleCheck = ({ item, key }: { item: option; key: any }) => {
        const element = document?.querySelector('#' + name + key) as HTMLInputElement | null;
        const checked = element?.checked;
        const list: option[] = [...selected];
        if (checked) {
            const index = list.indexOf(item);
            if (index > -1) {
                list.splice(index, 1);
            }
        } else {
            list.push(item);
        }
        setCurrentSelection(list);
        setSelected(list);
    };
    const handleCheckInput = ({ item, e }: { item: option; e: any }) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        const checked = !e.target.checked;
        const list: option[] = [...selected];
        if (checked) {
            const index = list.indexOf(item);
            if (index > -1) {
                list.splice(index, 1);
            }
        } else {
            list.push(item);
        }
        setCurrentSelection(list);
        setSelected(list);
    };
    const rowRenderer: ListRowRenderer = ({ key, index, style }) => {
        return (
            <>
                {Boolean(filteredList.length) && (
                    <Box key={'ListDiv_' + key} style={style} className={classNames(styles.pointer, styles.hoverBlue)} onClick={() => handleCheck({ item: filteredList[index], key })}>
                        <Checkbox
                            id={name + key}
                            key={'label_' + key}
                            name={name}
                            checked={selected.includes(filteredList[index])}
                            onClick={(e) => handleCheckInput({ item: filteredList[index], e })}
                        />
                        <Typography component={'label'} className={classNames(styles.pointer, styles.checkboxLabel)}>
                            {filteredList[index]['label']}
                        </Typography>
                    </Box>
                )}
            </>
        );
    };
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCheckAll();
        }
        if (e.keyCode === 27) {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setSearchTerm('');
        }
    };

    return (
        <>
            <Box className={styles.boxLayout}>
                <Box className={styles.searchBox}>
                    <Box className={styles.col1}>
                        <Input
                            disableUnderline={true}
                            fullWidth={true}
                            type="search"
                            disabled={displaySelected}
                            value={searchTerm}
                            id="default-search"
                            inputRef={textInput}
                            //className={displaySelected ? ' cursor-not-allowed' : ''}
                            placeholder="Rechercher"
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e)}
                            onClick={() => handleSearchClick()}
                            className={styles.inputSearch}
                        />
                    </Box>
                    <Box className={styles.col2}>
                        <Box className={styles.toolBar}>
                            <SGSTooltip title="Renseigner un terme de recherche pour filtrer automatiquement la liste">
                                <BtnTool className={styles.btnTool}>
                                    <SearchIcon className={styles.iconTool} />
                                </BtnTool>
                            </SGSTooltip>
                            {displayTools && (
                                <>
                                    <SGSTooltip title="Sélectionner toutes les options affichées">
                                        <BtnTool onClick={handleCheckAll} className={styles.btnTool}>
                                            <CheckIcon className={styles.iconTool} />
                                        </BtnTool>
                                    </SGSTooltip>
                                    <SGSTooltip title="Désélectionner toutes les options affichées">
                                        <BtnTool onClick={handleUnCheckAll} className={styles.btnTool}>
                                            <XIcon className={styles.iconTool} />
                                        </BtnTool>
                                    </SGSTooltip>
                                    <SGSTooltip title={displaySelected ? 'Afficher toutes les options' : 'Voir les options sélectionnées'}>
                                        <BtnTool onClick={handleDisplaySelected} className={styles.btnTool}>
                                            {displaySelected ? <EyeOffIcon className={classNames(styles.iconTool, styles.selected)} /> : <EyeIcon className={styles.iconTool} />}
                                        </BtnTool>
                                    </SGSTooltip>
                                </>
                            )}
                        </Box>
                    </Box>
                    {displayTools && (
                        <Box className={styles.col3}>
                            <Box className={styles.searchSelection}>
                                Sélection :{' '}
                                <span className={styles.result}>
                                    {selected?.length ? selected?.length : 0} sur {list.length}
                                </span>
                            </Box>
                        </Box>
                    )}
                </Box>
                {displaySelected && !selected.length && !isLoading ? (
                    <NoOptionSelected onClick={handleDisplaySelected} label="Choisir les options" />
                ) : (
                    <Box className={classNames(styles.list, styles.blueBkg)}>
                        <Box className={styles.linearProgress}>{isLoading && <LinearProgress />}</Box>
                        {isLoading ? (
                            <Box className={styles.noOption}>Chargement ...</Box>
                        ) : hasError ? (
                            <Box className={styles.noOption}>Une erreur est survenue !</Box>
                        ) : !filteredList.length ? (
                            <Box className={styles.noOption}>{noOptionLabel}</Box>
                        ) : (
                            ''
                        )}
                        <List id={name} width={660} height={200} rowCount={filteredList.length} rowRenderer={rowRenderer} rowHeight={35} className={classNames(styles.list, styles.scrollbar)} />
                    </Box>
                )}
            </Box>
        </>
    );
};
